import { useEffect, useState } from 'react'
import { tokenStore } from '@/lib/api'

/**
 * URL текстуры скина, пригодный для CSS-фона.
 *
 * Зачем вообще хук, а если проще — почему нельзя просто подставить
 * `/api/skins/:uuid.png` в `background-image`. Роут требует Bearer: панель не
 * должна работать открытым прокси к Mojang для всего интернета. А браузер к
 * фоновой картинке заголовков не добавляет — в проде такие аватарки отдавали бы
 * 401. В dev это маскировал сервис-воркер моков, который отвечает на всё.
 *
 * Поэтому скин качаем сами (с заголовком) и отдаём blob-URL. Кэш на уровне
 * модуля: на дашборде десятки плиток, и одна и та же голова не должна
 * скачиваться десять раз. Blob-URL не отзываем — их единицы на сессию, а отзыв
 * сломал бы уже отрендеренные плитки.
 */
const cache = new Map<string, Promise<string | null>>()

function load(url: string): Promise<string | null> {
  const cached = cache.get(url)
  if (cached !== undefined) return cached

  const promise = (async (): Promise<string | null> => {
    try {
      const token = tokenStore.get()
      const response = await fetch(url, {
        headers: token === null ? {} : { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) return null
      return URL.createObjectURL(await response.blob())
    } catch {
      // Скин — украшение: если он не пришёл, аватарка просто останется пустой,
      // и ронять из-за этого рендер плитки нельзя.
      return null
    }
  })()

  cache.set(url, promise)
  return promise
}

export function useSkinUrl(uuid: string | null, name: string): string | null {
  const url = `/api/skins/${uuid ?? 'offline'}.png?name=${encodeURIComponent(name)}`
  const [resolved, setResolved] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    void load(url).then(value => {
      if (alive) setResolved(value)
    })
    return () => {
      alive = false
    }
  }, [url])

  return resolved
}
